import type { TrainingBlock, TrainingDecision } from '@cadencia/contracts';
import {
  continuedBlockRecord,
  performanceTrend,
  priorityFor,
  REVIEW_WINDOWS,
  MUSCLES,
  proposalFromPhase,
  selectBlockPhase,
  type Muscle,
  type PhaseDecision,
} from '@cadencia/domain';
import { logger } from '../../lib/logger.js';
import { addDays, diaLocalDe, parseLocalDate, todayLocal } from '../../lib/dates.js';
import { db } from '../db.js';
import {
  attachDecisionInputs,
  listRecentDecisions,
  saveDecisions,
} from '../repositories/decisionsRepo.js';
import { proponerBloque, proposeBlock } from './blockProposalService.js';
import type { CoachAIProvider } from '@cadencia/domain';
import {
  blockDaysLeft,
  closeBlock,
  createBlockForPhase,
  getActiveBlock,
} from '../repositories/trainingRepo.js';
import { leerPreferencias } from '../preferenciaIA.js';
import { nowIso } from '../ids.js';
import type { WriteContext } from '../writes.js';

/**
 * Encadenado de bloques: que pasa cuando se acaba uno (§2.2, §2.3).
 *
 * Sin esto, terminar las cuatro semanas de readaptacion dejaba la aplicacion en
 * un callejon sin salida: nada ponia el bloque en `completed`, y como solo se
 * ofrece crear bloque cuando NO hay ninguno activo, la pantalla de entrenar se
 * quedaba vacia para siempre.
 *
 * La regla que ordena todo esto viene del §10: cambiar de fase SIEMPRE requiere
 * aprobacion. Asi que el bloque agotado no se cierra hasta que el usuario dice
 * que si; mientras tanto sigue activo pero sin semanas, y la propuesta espera en
 * el panel. Cerrarlo antes dejaria al usuario sin plan y sin haberlo decidido.
 *
 * La excepcion es seguir en la MISMA fase: eso no es un cambio, es continuar, y
 * se encadena solo.
 */

/** Semanas de historial que se miran para la tendencia. La ventana es del dominio (E4). */
const SEMANAS_DE_TENDENCIA = REVIEW_WINDOWS.blockTrendWeeks;

export interface BlockChainOutcome {
  /**
   * El bloque que se creo lleva la estructura que diseño la IA.
   *
   * `false` cuando la IA estaba apagada, no contesto o su estructura no paso
   * las reglas del dominio. El §13 y el §20 del encargo piden que el respaldo
   * sea IDENTIFICABLE: un bloque determinista no puede presentarse como
   * «revisado por IA», y sin este campo la pantalla no tendria forma de
   * distinguirlos.
   */
  aiDesigned: boolean;
  /** `null` si el bloque en curso todavia tiene semanas. */
  decision: PhaseDecision | null;
  /** Bloque nuevo, cuando se pudo encadenar sin preguntar. */
  created: TrainingBlock | null;
  /** Propuesta de cambio de fase a la espera de respuesta. */
  proposed: TrainingDecision | null;
  reasonCodes: string[];
}

const NADA: BlockChainOutcome = {
  aiDesigned: false,
  decision: null,
  created: null,
  proposed: null,
  reasonCodes: ['block_still_running'],
};

/**
 * Reune lo que el arbol de `selectBlockPhase` necesita.
 *
 * DOS FAMILIAS, Y LA DIFERENCIA IMPORTA
 *
 * * Lo DECLARADO: objetivo, contexto de recuperacion y prioridades. Sale de lo
 *   que el usuario ha dicho, no de lo que una bascula insinua.
 * * Lo OBSERVADO: tendencia de e1RM y estabilidad del readiness. Sale de lo que
 *   se ha entrenado.
 *
 * QUE DESAPARECIO EL 21/09/2026
 *
 * `bodyFatPct`, `waistCm`, `heightCm` y `nutritionAdherence`. Las tres primeras
 * imponian una fase —y con ella un deficit calorico— desde una cinta metrica;
 * la cuarta medía adherencia a un plan de comidas que ya no existe. El §6 del
 * encargo lo prohibe expresamente.
 *
 * Y con ellas se fue `muscleGainGoalActive: false`, que llevaba fijo desde la
 * Fase 3 con un comentario que decia la verdad: «no hay donde declarar un
 * objetivo de masa». Consecuencia: la rama de ganancia muscular del arbol era
 * inalcanzable, y el arbol contestaba mantenimiento para siempre. Ahora el
 * objetivo se lee de `user_preferences.training_goal`, que el usuario escribe.
 */
async function señales(
  ctx: WriteContext,
  block: TrainingBlock,
  today: string,
): Promise<Parameters<typeof selectBlockPhase>[0]> {
  const perfil = (await db.profiles.where('userId').equals(ctx.userId).toArray()).find(
    (fila) => fila.deletedAt === null,
  );

  const desde = addDays(today, -7 * SEMANAS_DE_TENDENCIA);
  /*
   * Se acota por `performedAt`, que esta indexado, en vez de traer TODAS las
   * series del usuario y filtrarlas en memoria (PERF-001).
   *
   * El limite es exacto, no aproximado: `parseLocalDate` da la medianoche
   * LOCAL de ese dia y su instante en UTC es justo el primero que puede tener
   * un `performedAt` de ese dia local. Asi el conjunto que devuelve el indice
   * es el mismo que devolvia el filtro.
   *
   * El filtro en memoria se queda, y no sobra: sigue comprobando `userId` y
   * `deletedAt`, que el indice no mira. La comparacion por dia local tambien
   * se conserva —es la que manda— para que el indice sea una optimizacion y no
   * una redefinicion de lo que entra.
   */
  const series = (
    await db.performedSets.where('performedAt').aboveOrEqual(parseLocalDate(desde).toISOString()).toArray()
  ).filter(
    (fila) =>
      fila.userId === ctx.userId &&
      fila.deletedAt === null &&
      diaLocalDe(fila.performedAt) >= desde,
  );

  // e1RM medio por semana, para la tendencia. Con menos de dos semanas
  // `performanceTrend` devuelve `unknown`, que es lo correcto.
  const porSemana: number[] = [];
  for (let atras = SEMANAS_DE_TENDENCIA - 1; atras >= 0; atras -= 1) {
    const inicio = addDays(today, -7 * (atras + 1));
    const fin = addDays(today, -7 * atras);
    const deLaSemana = series.filter((fila) => {
      const dia = diaLocalDe(fila.performedAt);
      return dia >= inicio && dia < fin;
    });
    if (deLaSemana.length === 0) continue;
    porSemana.push(deLaSemana.reduce((suma, fila) => suma + fila.e1rmKg, 0) / deLaSemana.length);
  }

  const checkins = (await db.readinessCheckins.where('userId').equals(ctx.userId).toArray()).filter(
    (fila) => fila.deletedAt === null && fila.forDate >= desde,
  );
  // «Estable» = no viene cayendo. Sin check-ins no se supone inestabilidad: eso
  // bloquearia una perdida de grasa por no haber rellenado un formulario.
  const readinessStable =
    checkins.length === 0 ||
    performanceTrend(
      [...checkins]
        .sort((a, b) => a.forDate.localeCompare(b.forDate))
        .map((fila) => fila.readinessScore),
    ) !== 'falling';

  const semanasParado =
    perfil?.detrainedSince == null
      ? 0
      : Math.max(
          0,
          Math.round(
            (new Date(`${today}T00:00:00Z`).getTime() -
              new Date(`${perfil.detrainedSince}T00:00:00Z`).getTime()) /
              (7 * 86_400_000),
          ),
        );

  /*
   * El objetivo y el contexto los DECLARA el usuario, y hay que distinguir «no
   * ha declarado nada» de «todavia no lo he mirado» (NEW5-002). La regla vive
   * en un solo sitio, `preferenciaIA.ts`, y se reutiliza tal cual: en un
   * dispositivo recien estrenado la fila puede existir en el servidor diciendo
   * lo contrario de lo que asume el defecto.
   *
   * Mientras no se sepa, el objetivo viaja como `null` —no como
   * «mantenimiento»— para que el arbol pueda dar un motivo que no mienta:
   * `goal_not_known_yet` y no `declared_maintenance_goal`. Las dos llevan al
   * mismo bloque prudente; solo una se arregla esperando a que baje la fila, y
   * quien lea el motivo tiene que poder distinguirlas.
   */
  const preferencias = await leerPreferencias(ctx.userId);
  const goal = preferencias.conocida
    ? (preferencias.fila?.trainingGoal ?? 'muscle_gain')
    : null;
  const recovery = preferencias.conocida
    ? (preferencias.fila?.recoveryContext ?? 'normal')
    : 'normal';

  /*
   * Bloques de DESARROLLO encadenados justo antes de este.
   *
   * Se cuenta hacia atras desde el actual y se para en el primero que no
   * acumula: una transicion o un mantenimiento en medio reinician la cuenta,
   * porque para eso estan. Se mira el historial guardado y no un contador en
   * la fila del bloque —una columna nueva en una tabla que sincroniza es la
   * trampa de R-16— porque el dato se deriva exacto de lo que ya hay.
   */
  const ACUMULAN = new Set(['muscle_gain', 'specialization', 'recomposition']);
  const bloques = (await db.trainingBlocks.where('userId').equals(ctx.userId).toArray())
    .filter((fila) => fila.deletedAt === null && fila.startedOn <= block.startedOn)
    .sort((a, b) => b.startedOn.localeCompare(a.startedOn));
  let seguidos = 0;
  for (const fila of bloques) {
    if (!ACUMULAN.has(fila.phase)) break;
    seguidos += 1;
  }

  return {
    weeksDetrained: semanasParado,
    // Ya se ha entrenado un bloque entero: no es el primero tras la inactividad.
    firstBlockAfterInactivity: false,
    goal,
    recovery,
    // Las prioridades son del bloque que termina: son del usuario y se
    // conservan al encadenar.
    hasPriorityMuscles: MUSCLES.some(
      (muscle) => priorityFor(muscle as Muscle, block.musclePriorities) === 'high',
    ),
    readinessStable,
    performance: performanceTrend(porSemana),
    consecutiveDevelopmentBlocks: seguidos,
    previousPhase: block.phase,
  };
}

/**
 * Mira si el bloque en curso se ha agotado y decide que viene despues.
 *
 * Se llama al abrir la pantalla de entrenar, igual que la materializacion de la
 * semana: es el momento en el que hace falta saberlo.
 */
export async function reviewBlockEnd(
  ctx: WriteContext,
  today = todayLocal(),
  opciones: { ia?: CoachAIProvider } = {},
): Promise<BlockChainOutcome> {
  const block = await getActiveBlock(ctx.userId);
  if (!block) return { ...NADA, reasonCodes: ['no_active_block'] };
  if (blockDaysLeft(block, today) > 0) return NADA;

  const decision = selectBlockPhase(await señales(ctx, block, today));

  // Misma fase: no hay nada que aprobar, se encadena.
  if (decision.change === null) {
    /*
     * AQUI ES DONDE LA IA ENTRA EN EL CAMINO AUTOMATICO.
     *
     * Hasta el 22/09/2026 esta rama creaba el bloque con la plantilla y no
     * preguntaba nada: encadenar era el caso mas frecuente —el que pasa cuando
     * el atleta sigue con el mismo objetivo— asi que la mayoria de los bloques
     * de la vida de un usuario nacian sin IA aunque la tuviera encendida y
     * pagada. El §7 del encargo no lo admite: «toda programacion inicial y todo
     * nuevo bloque deben incorporar su analisis y propuesta estructurada».
     *
     * POR QUE SE ESPERA A LA RESPUESTA. Esto corre al abrir Entrenar, asi que
     * el usuario espera. Se acepta porque pasa UNA vez cada 4-8 semanas —solo
     * cuando un bloque se agota— y lo que se gana es el bloque entero. La
     * alternativa —crear el determinista y ofrecer rediseñarlo despues—
     * incumple el §7 y ademas obliga a reescribir un bloque ya materializado.
     *
     * Y NO SE CUELGA SI NO HAY IA: `proposeBlock` devuelve `null` cuando esta
     * apagada, sin presupuesto o sin red, y entonces esto es exactamente lo que
     * era antes. La regla de «guardar nunca espera a la red» sigue intacta:
     * quien espera es este servicio, no el registro de una serie.
     */
    const pedida = opciones.ia
      ? await proponerBloque(opciones.ia, ctx.userId, decision.phase, {
          ...(block.musclePriorities ? { musclePriorities: block.musclePriorities } : {}),
          phaseReasonCodes: decision.reasonCodes,
        })
      : null;
    const propuesta = pedida?.propuesta ?? null;

    // Otra vez, DESPUÉS de la IA: si en ese rato otro dispositivo encadenó —la
    // bajada trae su bloque nuevo y el viejo cerrado—, esta respuesta es sobre
    // un estado viejo y crear aquí dejaría dos bloques activos (24/09/2026).
    const vigente = await getActiveBlock(ctx.userId);
    if (vigente?.id !== block.id) {
      return { ...NADA, reasonCodes: ['block_changed_meanwhile'] };
    }

    const disenadoPorIA = propuesta?.sessionPlan != null;

    const { block: nuevo } = await createBlockForPhase(ctx, decision.phase, {
      startDate: today,
      musclePriorities: block.musclePriorities,
      ...(propuesta
        ? {
            weeks: propuesta.weeks,
            volumeTargets: propuesta.targets,
            ...(propuesta.sessionPlan ? { sessionPlan: propuesta.sessionPlan } : {}),
          }
        : {}),
    });
    // El bloque viejo se cierra AQUI y no antes: si la creacion del nuevo
    // fallara, cerrarlo primero dejaria al usuario sin ninguno.
    await closeBlock(ctx, block, today);

    /*
     * Y queda escrito que se decidio continuar, con sus motivos (E4).
     *
     * Hasta hoy cambiar de fase dejaba una decision y seguir en la misma no
     * dejaba nada, asi que el historial solo contaba lo que cambiaba: mirando
     * atras no habia forma de saber por que el tercer bloque fue igual que el
     * segundo. Va DESPUES de crear el bloque porque registra un hecho, no una
     * intencion; si fallara, el bloque ya existe y el usuario tiene plan.
     */
    await saveDecisions(ctx, [
      continuedBlockRecord(
        {
          previousPhase: block.phase,
          decision,
          aiDesigned: propuesta?.sessionPlan != null,
          priorityMuscles: MUSCLES.filter(
            (muscle) => priorityFor(muscle as Muscle, block.musclePriorities) === 'high',
          ) as Muscle[],
        },
        nowIso(),
      ),
    ]);

    logger.info('engine', 'BLOCK_CHAINED', 'Bloque encadenado sin cambio de fase', {
      phase: decision.phase,
      disenadoPorIA,
      // Por qué no la diseñó la IA, si se le pidió: la decisión ya dice
      // `structure_from_template`, y esto dice la causa.
      motivoSinIA: pedida?.motivo ?? null,
    });
    return {
      aiDesigned: disenadoPorIA,
      decision,
      created: nuevo,
      proposed: null,
      reasonCodes: [
        'same_phase_continues',
        disenadoPorIA ? 'structure_designed_by_ai' : 'structure_from_template',
      ],
    };
  }

  // Cambio de fase: espera aprobacion (§10). El bloque viejo NO se cierra
  // todavia; cerrarlo dejaria al usuario sin plan por una decision que aun no
  // ha tomado.
  const yaPropuesto = (await listRecentDecisions(ctx.userId, 50)).some(
    (fila) =>
      fila.decisionType === 'phase_change' &&
      fila.approvalStatus === 'pending' &&
      fila.deletedAt === null,
  );
  if (yaPropuesto) {
    return {
      aiDesigned: false,
      decision,
      created: null,
      proposed: null,
      reasonCodes: ['phase_change_already_pending'],
    };
  }

  const propuesta = proposalFromPhase(decision, nowIso());
  if (propuesta === null) return { ...NADA, reasonCodes: ['nothing_to_propose'] };

  const [guardada] = await saveDecisions(ctx, [propuesta]);

  logger.info('engine', 'PHASE_CHANGE_PROPOSED', 'Cambio de fase a la espera', {
    phase: decision.phase,
    reasonCodes: decision.reasonCodes,
  });
  /*
   * Un cambio de estructura SIEMPRE espera aprobacion (§10), pero la propuesta
   * de la IA se pide YA y se guarda con la decision, en vez de esperar a que el
   * usuario pulse un boton.
   *
   * El §7 pide que todo bloque nuevo incorpore el analisis de la IA, y con el
   * boton eso dependia de que a alguien se le ocurriera pulsarlo. Ademas la
   * pantalla de aprobacion tiene asi algo que enseñar: el §12 exige comparar
   * situacion actual y propuesta, y sin la propuesta delante no hay comparacion
   * posible.
   *
   * Si falla, no pasa nada: la decision se guarda igual y el boton sigue ahi
   * para volver a intentarlo. Lo que no puede es tumbar el cambio de fase.
   */
  const propuestaIA = opciones.ia
    ? await proposeBlock(opciones.ia, ctx.userId, decision.phase, {
        ...(block.musclePriorities ? { musclePriorities: block.musclePriorities } : {}),
        phaseReasonCodes: decision.reasonCodes,
      })
    : null;

  if (propuestaIA !== null && guardada !== undefined) {
    await attachDecisionInputs(ctx, guardada.id, { aiBlockProposal: propuestaIA });
  }

  return {
    aiDesigned: propuestaIA?.sessionPlan != null,
    decision,
    created: null,
    proposed: guardada ?? null,
    reasonCodes: ['phase_change_needs_approval'],
  };
}
